import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Map, Upload, ArrowLeft, Pencil, Trash2, ZoomIn, ZoomOut, Maximize, MapPin, Loader2, Scale, CheckCircle2, Users } from "lucide-react";

const ZONES = [
  { key: "BZ", label: "Bluezone (BZ)" },
  { key: "GZ", label: "Greenzone (GZ)" },
];

function PlanImage({ planId, thumb, className, alt, onLoadDims }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null, cancelled = false;
    api.get(`/plans/${planId}/image${thumb ? "?thumb=true" : ""}`, { responseType: "blob" })
      .then((res) => { if (!cancelled) { url = URL.createObjectURL(res.data); setSrc(url); } })
      .catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [planId, thumb]);
  if (!src) return <div className={`${className} bg-slate-100 animate-pulse flex items-center justify-center`}><Map size={24} className="text-slate-300" /></div>;
  return <img src={src} alt={alt} className={className} draggable={false} onLoad={onLoadDims} />;
}

function StandInfoDialog({ stand, onClose }) {
  const [info, setInfo] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!stand) { setInfo(null); return; }
    api.get(`/plans/stand-info/${encodeURIComponent(stand.code)}`).then((r) => setInfo(r.data)).catch(() => setInfo({ matches: [] }));
  }, [stand]);
  return (
    <Dialog open={!!stand} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md" data-testid="stand-info-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <MapPin size={18} className="text-amber-600" /> Stand {stand?.code}
          </DialogTitle>
        </DialogHeader>
        {!info ? (
          <div className="py-6 text-center text-sm text-slate-400"><Loader2 size={18} className="animate-spin inline" /> Yükleniyor...</div>
        ) : info.matches.length === 0 ? (
          <p className="text-sm text-slate-500 py-2" data-testid="stand-no-match">Bu stand koduyla eşleşen proje bulunamadı.</p>
        ) : (
          <div className="space-y-3">
            {info.matches.map((m, i) => (
              <div key={i} className="border border-slate-200 p-3 space-y-1.5" data-testid={`stand-match-${i}`}>
                <div className="font-semibold text-sm">{m.project.name}</div>
                <div className="text-xs text-slate-500">{m.project.zone} · Stand {m.project.stand_no} · {m.project.project_type}</div>
                {m.winner && (
                  <div className="text-xs text-emerald-700 flex items-center gap-1.5">
                    <CheckCircle2 size={12} /> Kazanan: <strong>{m.winner.company}</strong> — {Number(m.winner.price).toLocaleString("tr-TR")} {m.winner.currency}
                  </div>
                )}
                {m.request_id && (
                  <Button size="sm" onClick={() => navigate(`/admin/teklifler/${m.request_id}`)} data-testid={`stand-goto-request-${i}`}
                          className="rounded-none h-8 mt-1 bg-slate-900 text-white hover:bg-slate-700 text-xs gap-1.5">
                    <Scale size={12} /> İhale / Karşılaştırmaya Git
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PlanViewer({ plan, onBack, onChanged }) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [editMode, setEditMode] = useState(false);
  const [supplierMode, setSupplierMode] = useState(false);
  const [assigned, setAssigned] = useState(null);
  const normCode = (c) => (c || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const toggleSupplierMode = async () => {
    if (!supplierMode && assigned === null) {
      try {
        const { data } = await api.get("/plans/assigned-stands");
        setAssigned(new Set(data.codes));
      } catch {
        setAssigned(new Set());
      }
    }
    setSupplierMode((v) => !v);
  };
  const [infoStand, setInfoStand] = useState(null);
  const [newStand, setNewStand] = useState(null);
  const [editStand, setEditStand] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [nameVal, setNameVal] = useState(plan.hub_name);
  const dragRef = useRef(null);
  const innerRef = useRef(null);

  const clamp = (v) => Math.min(5, Math.max(0.4, v));
  const onWheel = useCallback((e) => { e.preventDefault(); setScale((s) => clamp(s * (e.deltaY < 0 ? 1.12 : 0.89))); }, []);
  const viewRef = useRef(null);
  useEffect(() => {
    const el = viewRef.current;
    if (el) el.addEventListener("wheel", onWheel, { passive: false });
    return () => { if (el) el.removeEventListener("wheel", onWheel); };
  }, [onWheel]);

  const onPointerDown = (e) => {
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: offset.x, oy: offset.y, moved: false };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.sx, dy = e.clientY - dragRef.current.sy;
    if (Math.abs(dx) + Math.abs(dy) > 4) dragRef.current.moved = true;
    setOffset({ x: dragRef.current.ox + dx, y: dragRef.current.oy + dy });
  };
  const onPointerUp = () => { setTimeout(() => { dragRef.current = null; }, 0); };

  const onPlanClick = (e) => {
    if (dragRef.current?.moved) return;
    if (!editMode) return;
    const rect = innerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    setNewStand({ x, y, code: "" });
  };

  const saveNewStand = async () => {
    if (!newStand.code.trim()) return;
    try {
      await api.post(`/plans/${plan.id}/stands`, { code: newStand.code.trim(), x: newStand.x, y: newStand.y });
      toast.success("Stand eklendi");
      setNewStand(null);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveEditStand = async () => {
    try {
      await api.patch(`/plans/${plan.id}/stands/${editStand.id}`, { code: editStand.code.trim() });
      toast.success("Stand güncellendi");
      setEditStand(null);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeStand = async () => {
    try {
      await api.delete(`/plans/${plan.id}/stands/${editStand.id}`);
      toast.success("Stand silindi");
      setEditStand(null);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveName = async () => {
    try {
      await api.patch(`/plans/${plan.id}`, { hub_name: nameVal.trim() });
      toast.success("Hub adı güncellendi");
      setRenaming(false);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="plan-viewer">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} data-testid="plan-viewer-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200">
          <ArrowLeft size={15} /> Tüm Hub'lar
        </button>
        <h2 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2" data-testid="plan-viewer-title">
          {plan.hub_name}
          <button onClick={() => { setNameVal(plan.hub_name); setRenaming(true); }} data-testid="plan-rename-button" className="text-slate-400 hover:text-slate-700"><Pencil size={14} /></button>
        </h2>
        <span className="text-xs text-slate-400">{(plan.stands || []).length} stand işaretli{plan.ocr_status === "processing" ? " · OCR işleniyor..." : ""}</span>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setScale((s) => clamp(s * 1.25))} data-testid="plan-zoom-in" className="rounded-none h-8 w-8 p-0 border-slate-200"><ZoomIn size={14} /></Button>
          <Button size="sm" variant="outline" onClick={() => setScale((s) => clamp(s / 1.25))} data-testid="plan-zoom-out" className="rounded-none h-8 w-8 p-0 border-slate-200"><ZoomOut size={14} /></Button>
          <Button size="sm" variant="outline" onClick={() => { setScale(1); setOffset({ x: 0, y: 0 }); }} data-testid="plan-zoom-reset" className="rounded-none h-8 w-8 p-0 border-slate-200"><Maximize size={14} /></Button>
          <Button size="sm" onClick={toggleSupplierMode} data-testid="plan-supplier-toggle"
                  className={`rounded-none h-8 text-xs gap-1.5 ${supplierMode ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-100"}`}>
            <Users size={12} /> Tedarikçi
          </Button>
          <Button size="sm" onClick={() => setEditMode((v) => !v)} data-testid="plan-edit-toggle"
                  className={`rounded-none h-8 text-xs gap-1.5 ${editMode ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-slate-900 hover:bg-slate-700 text-white"}`}>
            <Pencil size={12} /> {editMode ? "Düzenleme Açık" : "Düzenle"}
          </Button>
        </div>
      </div>
      {supplierMode && (
        <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center gap-3" data-testid="plan-supplier-hint">
          <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-emerald-600 border border-emerald-700" /> Tedarikçi atanmış</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-white border border-slate-300" /> Tedarikçi atanmamış</span>
        </p>
      )}
      {editMode && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="plan-edit-hint">
          Düzenleme modu: Plan üzerinde boş bir noktaya tıklayarak stand ekleyin. Mevcut bir işarete tıklayarak kodu değiştirin veya silin.
        </p>
      )}
      <div ref={viewRef} className="border border-slate-200 bg-slate-50 overflow-hidden relative select-none" style={{ height: "calc(100vh - 260px)", minHeight: 420, cursor: dragRef.current ? "grabbing" : editMode ? "crosshair" : "grab" }}
           onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp} data-testid="plan-canvas">
        <div style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, transformOrigin: "center center", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div ref={innerRef} className="relative" onClick={onPlanClick} style={{ maxWidth: "100%", maxHeight: "100%" }}>
            <PlanImage planId={plan.id} alt={plan.hub_name} className="max-w-full max-h-[calc(100vh-280px)] block" />
            {(plan.stands || []).map((s) => (
              <button
                key={s.id}
                title={s.code}
                data-testid={`stand-marker-${s.code}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (dragRef.current?.moved) return;
                  if (editMode) setEditStand({ ...s });
                  else setInfoStand(s);
                }}
                className={`absolute -translate-x-1/2 -translate-y-1/2 px-1 py-0.5 text-[8px] leading-none font-bold border transition-colors duration-150 ${
                  supplierMode
                    ? (assigned?.has(normCode(s.code))
                        ? "bg-emerald-600/95 border-emerald-700 text-white hover:bg-emerald-500"
                        : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100")
                    : s.source === "manual" ? "bg-sky-600/90 border-sky-700 text-white hover:bg-sky-500" : "bg-amber-500/90 border-amber-600 text-white hover:bg-amber-400"
                }`}
                style={{ left: `${s.x * 100}%`, top: `${s.y * 100}%` }}
              >
                {s.code}
              </button>
            ))}
          </div>
        </div>
      </div>
      <StandInfoDialog stand={infoStand} onClose={() => setInfoStand(null)} />

      <Dialog open={!!newStand} onOpenChange={(o) => { if (!o) setNewStand(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-sm" data-testid="stand-add-modal">
          <DialogHeader><DialogTitle className="font-heading text-lg font-bold">Stand Ekle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Stand Kodu *</Label>
              <Input autoFocus value={newStand?.code || ""} onChange={(e) => setNewStand((v) => ({ ...v, code: e.target.value.toUpperCase() }))}
                     onKeyDown={(e) => { if (e.key === "Enter") saveNewStand(); }}
                     placeholder="örn. G2-014" data-testid="stand-add-code-input" className="rounded-none border-slate-200 h-10" />
            </div>
            <Button onClick={saveNewStand} disabled={!newStand?.code?.trim()} data-testid="stand-add-save" className="w-full rounded-none h-10 bg-slate-900 text-white hover:bg-slate-700">Kaydet</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editStand} onOpenChange={(o) => { if (!o) setEditStand(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-sm" data-testid="stand-edit-modal">
          <DialogHeader><DialogTitle className="font-heading text-lg font-bold">Stand Düzenle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Stand Kodu</Label>
              <Input value={editStand?.code || ""} onChange={(e) => setEditStand((v) => ({ ...v, code: e.target.value.toUpperCase() }))}
                     data-testid="stand-edit-code-input" className="rounded-none border-slate-200 h-10" />
            </div>
            <div className="flex gap-2">
              <Button onClick={saveEditStand} data-testid="stand-edit-save" className="flex-1 rounded-none h-10 bg-slate-900 text-white hover:bg-slate-700">Kaydet</Button>
              <Button onClick={removeStand} variant="outline" data-testid="stand-edit-delete" className="rounded-none h-10 border-red-200 text-red-600 hover:bg-red-50 gap-1.5"><Trash2 size={14} /> Sil</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={renaming} onOpenChange={setRenaming}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-sm" data-testid="plan-rename-modal">
          <DialogHeader><DialogTitle className="font-heading text-lg font-bold">Hub Adını Değiştir</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={nameVal} onChange={(e) => setNameVal(e.target.value)} data-testid="plan-rename-input" className="rounded-none border-slate-200 h-10" />
            <Button onClick={saveName} disabled={!nameVal.trim()} data-testid="plan-rename-save" className="w-full rounded-none h-10 bg-slate-900 text-white hover:bg-slate-700">Kaydet</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ZoneUpload({ zone, onDone }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("zone", zone);
      fd.append("file", file);
      await api.post("/plans/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Plan yüklendi, stand kodları otomatik tespit ediliyor...");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <div className="border border-dashed border-slate-300 bg-white px-6 py-16 text-center space-y-4" data-testid={`plan-upload-${zone.toLowerCase()}`}>
      <Upload size={32} className="mx-auto text-slate-300" />
      <div>
        <p className="font-semibold text-slate-700">Bu bölge için henüz plan yüklenmedi</p>
        <p className="text-sm text-slate-400 mt-1">Yerleşim planı PDF'ini yükleyin — her sayfa ayrı bir hub olarak işlenir, stand kodları OCR ile otomatik tespit edilir.</p>
      </div>
      <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`plan-upload-input-${zone.toLowerCase()}`} />
      <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`plan-upload-button-${zone.toLowerCase()}`} className="rounded-none h-10 bg-slate-900 text-white hover:bg-slate-700 gap-2">
        {busy ? <><Loader2 size={14} className="animate-spin" /> Yükleniyor...</> : <><Upload size={14} /> PDF Yükle</>}
      </Button>
    </div>
  );
}

export default function PlansPage() {
  const [zone, setZone] = useState("GZ");
  const [plans, setPlans] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/plans?zone=${zone}`);
      setPlans(r.data);
      setSelected((cur) => (cur ? r.data.find((p) => p.id === cur.id) || null : cur));
    } catch { /* noop */ }
    setLoaded(true);
  }, [zone]);

  useEffect(() => { setLoaded(false); setSelected(null); load(); }, [load]);
  const processing = plans.some((p) => p.ocr_status === "processing");
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [processing, load]);

  return (
    <div className="space-y-6" data-testid="plans-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Yerleşim</div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Planlar</h1>
      </div>
      <div className="flex border-b border-slate-200" data-testid="plans-zone-tabs">
        {ZONES.map((z) => (
          <button key={z.key} onClick={() => setZone(z.key)} data-testid={`plans-tab-${z.key.toLowerCase()}`}
                  className={`px-5 py-2.5 text-sm font-semibold -mb-px border-b-2 transition-colors duration-200 ${zone === z.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            {z.label}
          </button>
        ))}
      </div>
      {selected ? (
        <PlanViewer plan={selected} onBack={() => setSelected(null)} onChanged={load} />
      ) : !loaded ? (
        <div className="border border-slate-200 bg-white px-4 py-14 text-center text-slate-400">Yükleniyor...</div>
      ) : plans.length === 0 ? (
        <ZoneUpload zone={zone} onDone={load} />
      ) : (
        <>
          {processing && (
            <p className="text-xs text-sky-700 bg-sky-50 border border-sky-200 px-3 py-2 flex items-center gap-2" data-testid="plans-ocr-processing">
              <Loader2 size={13} className="animate-spin" /> Stand kodları OCR ile tespit ediliyor, liste otomatik güncellenecek...
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="plans-hub-grid">
            {plans.map((p) => (
              <button key={p.id} onClick={() => setSelected(p)} data-testid={`hub-card-${p.page_index}`}
                      className="border border-slate-200 bg-white text-left hover:border-slate-900 transition-colors duration-200 group">
                <div className="aspect-[16/10] overflow-hidden border-b border-slate-100 bg-slate-50">
                  <PlanImage planId={p.id} thumb alt={p.hub_name} className="w-full h-full object-contain" />
                </div>
                <div className="p-3.5">
                  <div className="font-heading font-bold tracking-tight group-hover:underline">{p.hub_name}</div>
                  <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5">
                    <MapPin size={11} /> {(p.stands || []).length} stand işaretli
                    {p.ocr_status === "processing" && <Loader2 size={11} className="animate-spin text-sky-500" />}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
